import type { GameCalibration } from '@/lib/gameMap'
import { gameXToWorld, gameYToWorld, worldToGame } from '@/lib/mapScale'

/**
 * Harita ızgarası — Fabric tuvaline, çizimlerden hemen önce çizilir
 * (`before:render`). Oyunun harita ekranının düzeni:
 *
 * - Çizgiler oyun koordinatına oturur: 1 km (10 birim), 100 m (1 birim), en
 *   yakında 10 m. Bir kademe ancak çizgileri arasında `MIN_PX` kadar yer
 *   varsa çizilir.
 * - Etiketler çerçevenin dışında: **Y solda** (yukarı doğru büyür), **X
 *   altta**, **sağda satırın kare boyu** (`1KM`, `100M`). Değerler oyunun
 *   yazdığı gibi: km kademesinde km (`0`…`16`), yakında oyun birimi. Çerçeve
 *   ekrandan taşınca şerit ekran kenarına yapışır.
 * - Yalnızca oynanabilir alan (`cal.bounds`) çizilir.
 *
 * **Neden tuvalde, DOM'da değil:** Fabric zoom/pan'in her karesinde tuvali
 * zaten baştan çiziyor; birkaç yüz çizgi ve birkaç düzine yazı 2D canvas'ta
 * milisaniyenin altında. DOM'daki ızgara (önce SVG, sonra gradyan arka plan +
 * yüzlerce etiket) her karede yeniden rasterize ediliyordu. Ölçüldü (DPR 2,
 * 1920×1080, yazılım boyama): ızgara açıkken 8.8 FPS, kapalıyken 25 FPS.
 */

const LEVELS = [
  { step: 10, stroke: 'rgba(255,255,255,0.4)', scale: '1KM' },
  { step: 1, stroke: 'rgba(255,255,255,0.2)', scale: '100M' },
  { step: 0.1, stroke: 'rgba(255,255,255,0.1)', scale: '10M' },
]
const MIN_PX = 10
const LABEL_PX = 40
const GAP = 8
const FONT = '700 13px Tektur, "IBM Plex Sans", ui-sans-serif, sans-serif'

function labelOf(v: number, step: number): string {
  if (step >= 10) return String(Math.round(v / 10))
  if (step >= 1) return String(Math.round(v))
  return v.toFixed(1)
}

/**
 * `ctx`: Fabric'in alt tuvali, viewport dönüşümü uygulanmamış (CSS pikseli).
 * `vpt`: Fabric'in `viewportTransform`'u. `w`/`h`: tuvalin CSS boyu.
 */
export function drawGrid(
  ctx: CanvasRenderingContext2D,
  vpt: number[],
  w: number,
  h: number,
  cal: GameCalibration,
  worldSize: number,
) {
  const a = vpt[0], e = vpt[4], f = vpt[5]
  if (!(a > 0)) return
  const b = cal.bounds
  const sx = (x: number) => gameXToWorld(x, cal, worldSize) * a + e
  const sy = (y: number) => gameYToWorld(y, cal, worldSize) * a + f
  const pxPerUnit = sx(1) - sx(0)

  const fl = sx(b.minX), fr = sx(b.maxX), ft = sy(b.maxY), fb = sy(b.minY)
  // Görünür kısım: ekran ∩ çerçeve, oyun koordinatında.
  const tl = worldToGame({ x: (Math.max(0, fl) - e) / a, y: (Math.max(0, ft) - f) / a }, cal, worldSize)
  const br = worldToGame({ x: (Math.min(w, fr) - e) / a, y: (Math.min(h, fb) - f) / a }, cal, worldSize)
  if (br.x <= tl.x || tl.y <= br.y) return
  const ticks = (lo: number, hi: number, step: number) => {
    const out: number[] = []
    // Tam sayıya yuvarlanmış indisle ilerlenir: 0.1 adımı toplanınca kayar.
    for (let i = Math.ceil(lo / step - 1e-9); i * step <= hi + 1e-9; i++) out.push(i * step)
    return out
  }
  const onCoarser = (v: number, li: number) =>
    LEVELS.slice(0, li).some(l => Math.abs(v / l.step - Math.round(v / l.step)) < 1e-6)
  const crisp = (v: number) => Math.round(v) + 0.5
  const lt = Math.max(0, ft), lb = Math.min(h, fb), ll = Math.max(0, fl), lr = Math.min(w, fr)

  ctx.save()
  ctx.lineWidth = 1
  // İnceden kalına: kalın çizgi ince olanın üstüne biner.
  for (let li = LEVELS.length - 1; li >= 0; li--) {
    const l = LEVELS[li]
    if (l.step * pxPerUnit < MIN_PX) continue
    ctx.strokeStyle = l.stroke
    ctx.beginPath()
    for (const x of ticks(tl.x, br.x, l.step)) {
      if (onCoarser(x, li)) continue
      const px = crisp(sx(x)); ctx.moveTo(px, lt); ctx.lineTo(px, lb)
    }
    for (const y of ticks(br.y, tl.y, l.step)) {
      if (onCoarser(y, li)) continue
      const py = crisp(sy(y)); ctx.moveTo(ll, py); ctx.lineTo(lr, py)
    }
    ctx.stroke()
  }
  // Haritanın çerçevesi — oyundaki gibi düz, ince.
  ctx.strokeStyle = 'rgba(255,255,255,0.55)'
  ctx.strokeRect(crisp(fl), crisp(ft), Math.round(fr - fl), Math.round(fb - ft))

  // Etiketlenen kademe: aralığı LABEL_PX'i geçen en ince kademe.
  let label = LEVELS[0]
  for (const l of LEVELS) if (l.step * pxPerUnit >= LABEL_PX) label = l
  ctx.font = FONT
  ctx.lineJoin = 'round'
  ctx.lineWidth = 3
  ctx.strokeStyle = 'rgba(0,0,0,0.85)'
  ctx.fillStyle = 'rgba(255,255,255,0.75)'
  const text = (s: string, x: number, y: number) => { ctx.strokeText(s, x, y); ctx.fillText(s, x, y) }

  const yLabelX = Math.max(28, fl - GAP)
  const scaleX = Math.min(w - 44, fr + GAP)
  ctx.textBaseline = 'middle'
  for (const y of ticks(br.y, tl.y, label.step)) {
    const py = sy(y)
    ctx.textAlign = 'right'; text(labelOf(y, label.step), yLabelX, py)
    ctx.textAlign = 'left'; text(label.scale, scaleX, py)
  }
  const xLabelY = Math.min(h - 6, fb + GAP + 13)
  ctx.textAlign = 'center'; ctx.textBaseline = 'bottom'
  for (const x of ticks(tl.x, br.x, label.step)) text(labelOf(x, label.step), sx(x), xLabelY)
  ctx.restore()
}
